import Flex from '@Components/Bases/Flex';
import { FormInput } from '@Components/Form';
import FormSelect from '@Components/Form/FormSelect';
import {
  disabledFormInputStyles,
  disabledFormSelectStyles,
} from '@Components/Form/styles';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import {
  IconButton,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import { IGreenhouseFormValues } from '@Router/EsgGreenhouse/Containers/EsgGreenhouseFormProvider/types';
import { arrange, asc, tidy } from '@tidyjs/tidy';
import { Control, useFormContext, useWatch } from 'react-hook-form';
import { SubcommitteeType } from 'src/Enum/EsgGreenhouse';
import { subcommitteeList } from './utils';

type SubcommiteeTypeListProps = {
  control: Control<IGreenhouseFormValues, unknown>;
  mode: 'create' | 'edit';
};

const SubcommiteeTypeList = ({ control, mode }: SubcommiteeTypeListProps) => {
  const { setValue, watch } = useFormContext();

  const [subcommittees, completedSection] = useWatch({
    name: [
      'climateChangeStructure.subcommitteeResponsibility.subcommittees',
      'climateChangeStructure.completedSection',
    ],
    control,
  });

  const prevSubcommitteesType = subcommittees?.map(
    (subcommittee) => subcommittee.subcommitteeType
  );

  const [subcommitteeType] = watch(['subcommitteeType']);

  const listOptions = tidy(subcommitteeList, arrange([asc('sequence')]));

  const filterScopeOptions = listOptions?.filter((option) => {
    if (option.subcommitteeType === SubcommitteeType.OTHER_SUBCOMMITTEE) {
      return true;
    }
    if (mode === 'edit' && option.subcommitteeType === subcommitteeType) {
      return true;
    }
    return !prevSubcommitteesType?.includes(option.subcommitteeType);
  });

  const displaySubcommittee = listOptions.find(
    (option) => option.subcommitteeType === subcommitteeType
  );

  const disableSubcommitteeInput =
    subcommitteeType === SubcommitteeType.OTHER_SUBCOMMITTEE;

  const { isCompletedTh, isCompletedEn } = completedSection ?? {};

  const handleSubcommitteeChange = (event: SelectChangeEvent<unknown>) => {
    const selectedId = event.target.value;

    setValue('subcommitteeType', selectedId);

    const selectedItem = subcommitteeList.find(
      (item) => item.subcommitteeType === selectedId
    );

    if (selectedItem) {
      setValue('nameTh', selectedItem.nameTh ?? '');
      setValue('nameEn', selectedItem.nameEn ?? '');
    }

    if (selectedId === SubcommitteeType.OTHER_SUBCOMMITTEE) {
      setValue('nameTh', '');
      setValue('nameEn', '');
    }
  };

  return (
    <>
      <Flex alignItems="baseline" justifyContent="end" mt={1} gap={2}>
        <Flex flexShrink={0} maxWidth={200} justifyContent={'flex-end'}>
          <InputLabel required>รายชื่อคณะกรรมการชุดย่อย</InputLabel>
          <Tooltip
            placement="top"
            arrow
            title="หากต้องการใช้ข้อมูลเกี่ยวกับรายชื่อที่เคยกรอก ของกรรมการบริษัท กรรมการชุดย่อย หรือ ผู้บริหารระดับสูง กรุณาเลือกรายชื่อ"
          >
            <IconButton size="small">
              <HelpOutlineOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Flex>

        <Flex gap={2} flexGrow={1} justifyContent={'flex-end'} maxWidth={600}>
          <FormSelect
            name="subcommitteeType"
            placeholder="โปรดเลือก"
            label="ภาษาไทย"
            fullWidth
            sx={
              isCompletedTh || isCompletedEn
                ? disabledFormSelectStyles
                : undefined
            }
            onChange={handleSubcommitteeChange}
          >
            {filterScopeOptions?.map((option) => (
              <MenuItem
                key={option.subcommitteeType}
                value={option.subcommitteeType}
              >
                {option.nameTh}
              </MenuItem>
            ))}
          </FormSelect>
          <FormInput
            label="ภาษาอังกฤษ"
            name={'subcommitteeTypeEn'}
            value={displaySubcommittee?.nameEn}
            sx={{
              ...disabledFormInputStyles,
              width: 1,
            }}
          />
        </Flex>
      </Flex>

      <Flex alignItems="baseline" justifyContent="end" mt={1} gap={2}>
        <Flex flexShrink={0} maxWidth={200} justifyContent={'flex-end'}>
          <InputLabel required>รายชื่อคณะกรรมการชุดย่อย</InputLabel>
        </Flex>

        <Flex gap={2} flexGrow={1} justifyContent={'flex-end'} maxWidth={600}>
          <FormInput
            name="nameTh"
            placeholder="กรุณาระบุชื่อคณะกรรมการชุดย่อย"
            sx={{
              width: 1,
            }}
            slotProps={{
              input: {
                disabled: !disableSubcommitteeInput || isCompletedTh,
              },
            }}
          />
          <FormInput
            name="nameEn"
            placeholder="กรุณาระบุชื่อคณะกรรมการชุดย่อย"
            sx={{
              width: 1,
            }}
            slotProps={{
              input: {
                disabled: !disableSubcommitteeInput || isCompletedEn,
              },
            }}
          />
        </Flex>
      </Flex>
    </>
  );
};

export default SubcommiteeTypeList;
